<template>
  <nav class="tabBar">
    <router-link
      v-for="tab in tabs"
      :key="tab.to"
      :to="tab.to"
      class="tabLink"
      :class="{ active: tab.isActive }"
    >
      {{ tab.label }}
    </router-link>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import type { PlayEngine } from '../composables/usePlayEngine'

// A Strategy play has no classic configuration, so it has no Details tab.
const props = defineProps<{
  playId: string
  engine?: PlayEngine
}>()

const route = useRoute()

const tabs = computed(() => {
  const base = `/sales-plays/${props.playId}`
  const path = route.path

  return [
    {
      label: 'Canvas',
      to: base,
      isActive: path === base || path === `${base}/`,
    },
    {
      label: 'Companies',
      to: `${base}/candidates/companies`,
      isActive: path.startsWith(`${base}/candidates/companies`),
    },
    {
      label: 'People',
      to: `${base}/candidates/people`,
      isActive: path.startsWith(`${base}/candidates/people`),
    },
    {
      label: 'Details',
      to: `${base}/details`,
      isActive: path === `${base}/details`,
    },
  ].filter((tab) => props.engine !== 'flock' || tab.label !== 'Details')
})
</script>

<style scoped>
.tabBar {
  display: flex;
  gap: var(--space-1);
  border-bottom: 1px solid var(--color-border-subtle);
  margin-bottom: var(--space-5);
}

.tabLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-tertiary);
  text-decoration: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  white-space: nowrap;
  transition: color var(--duration-fast) var(--ease-out),
              border-color var(--duration-fast) var(--ease-out);
}

.tabLink:hover {
  color: var(--color-text-primary);
}

.tabLink.active {
  color: var(--color-accent);
  border-bottom-color: var(--color-accent);
}
</style>
